import { useDispatch, useSelector } from 'react-redux';
import { selectCrmOrgAutomations } from '../../reducers/global/automationReducers';
import { useEffect, useMemo } from 'react';
import {
  setAutomationsPills,
  setAlertsPills,
  setAssignmentPills,
  setDocumentationPills,
  setDedupePills,
  setMatchingPills,
  setScheduledAssignmentPills,
} from './canvasPillsReducer';
import calculateSweepElementsPills from './calculateSweepElementsPills';
import { selectCrmOrgAlerts } from '../../reducers/global/alertsReducers';
import { selectCrmOrgAssignments } from '../../reducers/global/assignmentReducers';
import { calculateDocumentationPills } from './calculateDocumentationPills';
import {
  selectCrmOrgDedup,
  selectCrmOrgMatching,
} from '../../reducers/global/dedupMatchingReducers';
import { selectCrmOrgScheduledAssignments } from '../../reducers/global/scheduledAssignmentReducers';
import { getDeployedVersions } from '../documentation/selected-object/filters/utils';
import {
  selectOnlyActiveParsedRules,
  selectParsedRecordTypes,
} from '../../reducers/global/globalReducer';

export const useCalculatePills = ({
  crmOrgId,
  funnelMap,
}: {
  crmOrgId: string;
  funnelMap: FunnelMap;
}) => {
  const dispatch = useDispatch();

  const automations = useSelector(selectCrmOrgAutomations(crmOrgId));
  const alerts = useSelector(selectCrmOrgAlerts(crmOrgId));
  const assignments = useSelector(selectCrmOrgAssignments(crmOrgId));
  const scheduledAssignments = useSelector(selectCrmOrgScheduledAssignments(crmOrgId));
  const dedupe = useSelector(selectCrmOrgDedup(crmOrgId));
  const matching = useSelector(selectCrmOrgMatching(crmOrgId));

  const _automations = useMemo(() => getDeployedVersions(automations ?? []), [automations]);
  const _alerts = useMemo(() => getDeployedVersions(alerts ?? []), [alerts]);
  const _assignments = useMemo(() => getDeployedVersions(assignments ?? []), [assignments]);
  const _scheduledAssignments = useMemo(
    () => getDeployedVersions(scheduledAssignments ?? []),
    [scheduledAssignments],
  );
  const _dedupe = useMemo(() => getDeployedVersions(dedupe ?? []), [dedupe]);
  const _matching = useMemo(() => getDeployedVersions(matching ?? []), [matching]);

  const parsedRules = useSelector(selectOnlyActiveParsedRules(crmOrgId));
  const parsedRecordTypes = useSelector(selectParsedRecordTypes(crmOrgId));

  useEffect(() => {
    _automations && dispatch(setAutomationsPills(calculateSweepElementsPills(_automations)));
  }, [_automations, dispatch]);

  useEffect(() => {
    _alerts && dispatch(setAlertsPills(calculateSweepElementsPills(_alerts)));
  }, [_alerts, dispatch]);

  useEffect(() => {
    _assignments && dispatch(setAssignmentPills(calculateSweepElementsPills(_assignments)));
  }, [_assignments, dispatch]);

  useEffect(() => {
    _scheduledAssignments &&
      dispatch(setScheduledAssignmentPills(calculateSweepElementsPills(_scheduledAssignments)));
  }, [_scheduledAssignments, dispatch]);

  useEffect(() => {
    _dedupe && dispatch(setDedupePills(calculateSweepElementsPills(_dedupe)));
  }, [_dedupe, dispatch]);

  useEffect(() => {
    _matching && dispatch(setMatchingPills(calculateSweepElementsPills(_matching)));
  }, [_matching, dispatch]);

  useEffect(() => {
    if (funnelMap?.funnelsData && parsedRecordTypes && parsedRules) {
      dispatch(
        setDocumentationPills(
          calculateDocumentationPills({
            funnelsData: funnelMap.funnelsData,
            recordTypesData: funnelMap.recordTypesData || {},
            recordTypes: funnelMap.recordTypes || {},
            parsedRecordTypes,
            parsedRules,
          }),
        ),
      );
    }
  }, [
    dispatch,
    funnelMap?.funnelsData,
    funnelMap?.recordTypesData,
    funnelMap?.recordTypes,
    parsedRecordTypes,
    parsedRules,
    crmOrgId,
  ]);
};
